import { useEffect, useMemo } from "react";
import { AimOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import L from "leaflet";
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import styles from "./styles.module.css";

/**
 * نقشه‌ی انتخاب و نمایش محل کار.
 *
 * پیش از این کاشی‌ها و درگ و زوم دستی پیاده شده بودند و هر سه ایراد داشتند: درگ
 * می‌پرید، کلیک نقشه را وسط‌چین می‌کرد و چرخ ماوس یک‌باره یک سطح زوم می‌رفت. حالا
 * روی leaflet سوار است — همان کتابخانه‌ای که در pegah-erp هم استفاده می‌شود — و
 * این رفتارها دیگر کدِ ما نیستند.
 *
 * قرارداد props عمداً دست‌نخورده مانده تا صفحه‌های مصرف‌کننده (محل کار در حضور و
 * غیاب و کارت ثبت تردد) تغییری لازم نداشته باشند.
 */
const DEFAULT_CENTER = { latitude: 35.6892, longitude: 51.389 };

const validPoint = (point) =>
  point?.latitude !== null &&
  point?.latitude !== undefined &&
  point?.latitude !== "" &&
  point?.longitude !== null &&
  point?.longitude !== undefined &&
  point?.longitude !== "" &&
  Number.isFinite(Number(point.latitude)) &&
  Number.isFinite(Number(point.longitude));

const toLatLng = (point) => [Number(point.latitude), Number(point.longitude)];

/* آیکون‌ها divIcon‌اند نه تصویر: آیکون پیش‌فرض leaflet به فایل png وابسته است و با
   باندلر شکسته می‌شود، و شکل نشانگرها هم از قبل با CSS خودمان ساخته شده بود. */
const pinIcon = (className) =>
  L.divIcon({
    className: styles.pinWrapper,
    html: `<span class="${className}"></span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
  });

const dotIcon = (className) =>
  L.divIcon({
    className: styles.pinWrapper,
    html: `<span class="${className}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

/**
 * نقشه با هر تغییر `center` وسط‌چین نمی‌شود.
 *
 * انتخاب یک نقطه همان نقطه را به `center` برمی‌گرداند؛ با وسط‌چین‌کردن، نقشه درست
 * بعد از هر کلیک می‌پرید. فقط وقتی جابه‌جا می‌شویم که نقطه اصلاً در کادر نباشد —
 * یعنی «موقعیت فعلی من» یا اولین باز شدن.
 */
const Recenter = ({ center }) => {
  const map = useMap();
  const latitude = center?.latitude;
  const longitude = center?.longitude;

  useEffect(() => {
    if (!validPoint({ latitude, longitude })) return;
    const target = L.latLng(Number(latitude), Number(longitude));
    if (!map.getBounds().pad(-0.15).contains(target)) {
      map.setView(target, map.getZoom());
    }
  }, [map, latitude, longitude]);

  return null;
};

/**
 * leaflet اندازه‌ی کادر را فقط یک بار، موقع ساخته‌شدن، می‌خواند.
 *
 * این نقشه داخل مودال ساخته می‌شود و مودال هنوز در حال باز شدن است، پس آن لحظه
 * عرض و ارتفاع واقعی‌اش را ندارد: leaflet یک کادر کوچک فرض می‌کند و فقط کاشی‌های
 * همان گوشه را می‌گیرد. `invalidateSize` دوباره اندازه می‌گیرد — یک بار بعد از
 * باز شدن مودال و بعد از آن با هر تغییر اندازه‌ی کادر.
 */
const AutoResize = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);

    /* انیمیشن باز شدن مودال چند فریم طول می‌کشد و ResizeObserver ممکن است پیش از
       رسیدن به اندازه‌ی نهایی شلیک کند. */
    const timer = setTimeout(() => map.invalidateSize(), 300);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [map]);

  return null;
};

const ClickToSelect = ({ onSelect }) => {
  useMapEvents({
    click: (event) =>
      onSelect?.({ latitude: event.latlng.lat, longitude: event.latlng.lng }),
  });

  return null;
};

const Controls = ({ onLocate }) => {
  const map = useMap();

  return (
    <div className={styles.controls}>
      <button type="button" onClick={() => map.zoomIn()} aria-label="بزرگ‌نمایی">
        <PlusOutlined />
      </button>
      <button type="button" onClick={() => map.zoomOut()} aria-label="کوچک‌نمایی">
        <MinusOutlined />
      </button>
      {onLocate && (
        <button type="button" onClick={onLocate} aria-label="موقعیت فعلی">
          <AimOutlined />
        </button>
      )}
    </div>
  );
};

export default function OpenStreetMap({
  center,
  zoom = 16,
  workplaces = [],
  userLocation,
  selectedLocation,
  onSelect,
  onLocate,
  interactive = true,
  height = 340,
}) {
  /* فقط برای اولین رندر؛ جابه‌جایی‌های بعدی کار Recenter است. */
  const initialCenter = useMemo(
    () => (validPoint(center) ? toLatLng(center) : toLatLng(DEFAULT_CENTER)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div
      className={`${styles.map} ${onSelect ? styles.selectable : ""}`}
      style={{ height }}
    >
      <MapContainer
        center={initialCenter}
        zoom={zoom}
        className={styles.container}
        zoomControl={false}
        dragging={interactive}
        scrollWheelZoom={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        keyboard={interactive}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          maxNativeZoom={19}
          maxZoom={19}
        />

        <AutoResize />
        <Recenter center={center} />
        {onSelect && <ClickToSelect onSelect={onSelect} />}

        {workplaces.filter(validPoint).map((place) => (
          <Circle
            key={`radius-${place.id || place.title}`}
            center={toLatLng(place)}
            radius={Number(place.radius_meters || 0)}
            pathOptions={{
              color: "#1677ff",
              fillColor: "#1677ff",
              fillOpacity: 0.14,
              weight: 2,
            }}
          />
        ))}

        {workplaces.filter(validPoint).map((place) => (
          <Marker
            key={`pin-${place.id || place.title}`}
            position={toLatLng(place)}
            icon={pinIcon(styles.pin)}
          >
            <Tooltip direction="top" offset={[0, -18]}>{place.title}</Tooltip>
          </Marker>
        ))}

        {validPoint(selectedLocation) && (
          <Marker position={toLatLng(selectedLocation)} icon={pinIcon(styles.pinSelected)}>
            <Tooltip direction="top" offset={[0, -18]}>موقعیت انتخاب‌شده</Tooltip>
          </Marker>
        )}

        {validPoint(userLocation) && (
          <Marker position={toLatLng(userLocation)} icon={dotIcon(styles.dot)}>
            <Tooltip direction="top" offset={[0, -12]}>موقعیت فعلی شما</Tooltip>
          </Marker>
        )}

        {interactive && <Controls onLocate={onLocate} />}
      </MapContainer>

      {onSelect && <div className={styles.guide}>برای انتخاب محل روی نقشه کلیک کنید</div>}
    </div>
  );
}
